import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { faCalendar } from "@fortawesome/pro-light-svg-icons";
import { faFolder } from "@fortawesome/pro-regular-svg-icons";
import { faFolder as faFolderSolid } from "@fortawesome/pro-solid-svg-icons";
import {
  faBarsStaggered,
  faSignature,
} from "@fortawesome/sharp-light-svg-icons";
import {
  faArrowUpToLine,
  faBars,
  faGrid2,
  faImagePolaroid,
  faPalette,
  faPen,
  faPlus,
} from "@fortawesome/sharp-regular-svg-icons";
import { faAngleDown } from "@fortawesome/sharp-solid-svg-icons";
import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { omit } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { useMutation, useSubscription } from "urql";
import { useDebounceCallback } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";

import { GraphqlWsClientContext } from "@/components/graphql/GraphqlWsContext";
import {
  FilteringInput,
  LibraryObjectTypeFilter,
  SortingInput,
} from "@/components/graphql/schema.graphql";
import { getForgeSessionUrl } from "@/components/inference/utils";
import { SearchInputField } from "@/components/list/SearchInputField";
import { SortDirectionToggle } from "@/components/list/SortDirectionToggle";
import { SortFieldSelect } from "@/components/list/SortFieldSelect";
import { NavigationHeaderRow } from "@/components/nav/components";
import { PrimaryNavigationHeaderRow } from "@/components/nav/components/PrimaryNavigation";
import { SelectionDrawer } from "@/components/selection-sidebar/SelectionDrawer";
import {
  bindMenu,
  bindMenuTrigger,
  Dialog,
  DialogActions,
  DialogContent,
  Icon,
  IconButton,
  InfiniteScrollFetchingIndicator,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  useMenuState,
  useTheme,
} from "@/components/ui-v2";
import { ACCEPTED_DRIVE_TYPES } from "@/constants/dropzone";
import {
  EmptyPageInfo,
  PageInfo,
  ResponseData,
  useDownloadObjects,
  useInfinitePagination,
  useRequiredWorkspace,
  useShowSnackbar,
  useStyleActions,
  useUrqlResponseHandler,
} from "@/hooks";
import { CreateSession } from "@/hooks/queries.graphql";
import type { DownloadableObject } from "@/hooks/useDownloadObjects";
import { useExportObjects } from "@/hooks/useExportObjects";
import { getConnectionNodesTyped } from "@/utils/relay";

import { CreateAsset } from "../asset/queries.graphql";
import { DEFAULT_FORGE_SESSION_NAME } from "../forge/constants";
import { GalleryWindow } from "../gallery/GalleryWindow";
import { useDrive } from "./DriveContext";
import DriveItems from "./DriveItems";
import { DriveUploadQueue } from "./DriveUploadQueue/DriveUploadQueue";
import { useDriveUploadQueueStore } from "./DriveUploadQueue/state";
import {
  DriveFileFragment,
  GetDriveObjects,
  GetDriveObjectsQueryResult,
  GetDriveObjectsQueryVariables,
  MoveToDirectory,
  OnDriveFileUpdate,
} from "./queries.graphql";
import { DriveBreadcrumbs } from "./tools/DriveBreadcrumbs";
import {
  DriveDragOverlay,
  modifierStartFromActivator,
} from "./tools/DriveDragOverlay";
import { DriveEmptyState } from "./tools/DriveEmptyState";
import { DriveNewDirectoryDialog } from "./tools/DriveNewDirectoryDialog";
import {
  DownloadOptions,
  DriveItem,
  DriveSortField,
  ItemTypeFilterType,
} from "./types";

const NB_ITEMS_PER_ROW = 8;
const NB_INITIAL_ROWS = 8;

const sensorOptions = {
  activationConstraint: {
    delay: 150,
    tolerance: 5,
  },
};

const viewTypes = [
  {
    id: "grid",
    tooltip: "Grid View",
    icon: <Icon style={{ fontSize: "13px" }} icon={faGrid2} />,
  },
  {
    id: "table",
    tooltip: "Table View",
    icon: <Icon style={{ fontSize: "13px" }} icon={faBars} />,
  },
];

const applyPagination = (
  vars: GetDriveObjectsQueryVariables,
  after: string | null,
) => ({
  ...vars,
  getObjectsInput: {
    ...vars.getObjectsInput,
    after,
  },
});

const extractQueryItems = ({
  workspace,
}: ResponseData<GetDriveObjectsQueryResult>): {
  pageInfo: PageInfo;
  items: DriveItem[];
} => {
  if (!workspace) {
    return {
      pageInfo: EmptyPageInfo,
      items: [],
    };
  }
  const rawPageInfo = workspace.library?.pageInfo;
  const pageInfo: PageInfo = rawPageInfo
    ? {
        hasNextPage: rawPageInfo.hasNextPage,
        hasPreviousPage: rawPageInfo.hasPreviousPage,
        startCursor: rawPageInfo.startCursor ?? undefined,
        endCursor: rawPageInfo.endCursor ?? undefined,
      }
    : EmptyPageInfo;
  return {
    pageInfo,
    items: getConnectionNodesTyped(workspace?.library).filter(
      (item) => item.__typename !== "Blueprint" && item.__typename !== "Skill",
    ),
  };
};

interface SortFieldData {
  id: DriveSortField;
  field: string;
  icon: React.ReactElement;
  label: string;
}

const sortFieldData: Array<SortFieldData> = [
  {
    id: "rank",
    field: "rank",
    icon: <Icon icon={faBarsStaggered} />,
    label: "Relevance",
  },
  {
    id: "created_at",
    field: "created_at",
    icon: <Icon icon={faCalendar} style={{ transform: "translateX(1px)" }} />,
    label: "Date Created",
  },
  {
    id: "name",
    field: "name",
    icon: <Icon icon={faSignature} />,
    label: "Name",
  },
];

const sortFieldDataByType = sortFieldData.reduce(
  (acc, next) => {
    acc[next.id] = next;
    return acc;
  },
  {} as Record<DriveSortField, SortFieldData>,
);

const ITEM_TYPE_FILTER_TO_OBJECT_TYPE: {
  [key in ItemTypeFilterType]: LibraryObjectTypeFilter | undefined;
} = {
  ["ALL"]: undefined,
  ["file"]: "FILE",
  ["asset"]: "ASSET",
  ["style"]: "STYLE",
};

const DropzoneOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  backdropFilter: "blur(12px)",
  backgroundColor: theme.palette.secondary["9/75"],
  color: theme.palette.secondary[3],
}));

const DropzoneDialogContent = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(8, 6),
  border: "2px dotted",
  borderRadius: 8,
  transition: "all 300ms ease-in-out",
  cursor: "pointer",
  color: theme.palette.secondary[3],
  "&:hover": {
    color: theme.palette.secondary[1],
    borderColor: theme.palette.secondary["6/50"],
  },
}));

type ViewLocation = "dialog" | "file-picker" | "drive";

interface DriveManagerProps {
  viewLocation?: ViewLocation;
}

export const DriveManager = (props: DriveManagerProps) => {
  const { viewLocation = "drive" } = props;

  const {
    currentDirectoryPath,
    selectedItems,
    setSelectedItems,
    onActivateItem,
    setSearchParams,
    driveTabType,
    itemTypeFilter,
    viewType,
    searchText,
    sortField,
    sortDirection,
    isPreviewOpen,
    setIsPreviewOpen,
    previewIndex,
    contentType,
  } = useDrive();

  const { workspace } = useRequiredWorkspace();
  const navigate = useNavigate();
  const { client: wsClient } = useContext(GraphqlWsClientContext);

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const isLarge = useMediaQuery(theme.breakpoints.down("xl"));

  const isFiltered =
    itemTypeFilter !== "ALL" || !!searchText?.length || !!contentType?.length;

  const previewObject =
    isPreviewOpen && previewIndex != null && selectedItems?.length
      ? selectedItems[previewIndex]
      : null;

  const filteringInputs = useMemo<FilteringInput[]>(() => {
    const inputs: Array<FilteringInput> = [];
    if (searchText?.length) {
      inputs.push({
        field: "fulltext",
        operator: "search",
        value: searchText,
      });
    }
    const objectTypes: string[] = [];
    const objectType = ITEM_TYPE_FILTER_TO_OBJECT_TYPE[itemTypeFilter];
    if (driveTabType === "explorer") {
      objectTypes.push("DIRECTORY");
    }
    if (objectType) {
      objectTypes.push(objectType);
    } else {
      objectTypes.push("FILE", "ASSET", "STYLE");
    }
    inputs.push({
      field: "type",
      operator: "in",
      value: JSON.stringify(objectTypes),
    });
    if (contentType?.length) {
      inputs.push({
        field: "file_content_type",
        operator: "in",
        value: JSON.stringify(contentType),
      });
    }
    return inputs;
  }, [searchText, itemTypeFilter, contentType, driveTabType]);

  const sortingInputs = useMemo<SortingInput[]>(() => {
    const inputs: Array<SortingInput> = [];
    if (sortField && sortDirection) {
      inputs.push({
        field: sortFieldDataByType[sortField].field,
        direction: sortDirection === "asc" ? "ASC" : "DESC",
      });
    }
    return inputs;
  }, [sortField, sortDirection]);

  const queryArgs = useMemo(() => {
    return {
      workspaceName: workspace.name,
      directory: driveTabType === "explorer" ? currentDirectoryPath : undefined,
      filtering: filteringInputs.length ? filteringInputs : undefined,
      sorting: sortingInputs.length ? sortingInputs : undefined,
    };
  }, [
    workspace.name,
    currentDirectoryPath,
    driveTabType,
    filteringInputs,
    sortingInputs,
  ]);

  const variables = useMemo(
    () => ({
      workspaceName: queryArgs.workspaceName,
      getObjectsInput: {
        directory: queryArgs.directory,
        filtering: queryArgs.filtering,
        sorting: queryArgs.sorting,
        onlyDirectoryObjects: true,
        first: NB_ITEMS_PER_ROW * NB_INITIAL_ROWS,
      },
    }),
    [queryArgs],
  );

  const {
    items,
    isFetching,
    pageInfo: { hasNextPage: hasMorePages },
    execute: reloadObjects,
    fetchMore: fetchMoreItems,
  } = useInfinitePagination({
    query: GetDriveObjects,
    variables,
    applyPagination,
    extractQueryItems,
  });

  const [, moveToDirectory] = useMutation(MoveToDirectory);
  const {
    createStyle: { execute: createStyle },
  } = useStyleActions();
  const [, createAsset] = useMutation(CreateAsset);
  const [, createSession] = useMutation(CreateSession);
  const { getResponseFromState } = useUrqlResponseHandler();
  const { downloadObjects } = useDownloadObjects();
  const { canExportObjects, exportObjects } = useExportObjects();

  const { showError } = useShowSnackbar();

  const { addFilesToQueue } = useDriveUploadQueueStore();

  const [isNewFileDialogOpen, setIsNewFileDialogOpen] = useState(false);
  const [isNewDirectoryDialogOpen, setIsNewDirectoryDialogOpen] =
    useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      addFilesToQueue(acceptedFiles);
      setIsNewFileDialogOpen(false);
    },
    [addFilesToQueue, setIsNewFileDialogOpen],
  );

  const {
    getRootProps: getDropzoneRootProps,
    getInputProps: getDropzoneInputProps,
    isDragActive: isDropzoneDragActive,
    isDragAccept: isDropzoneDragAccept,
    isDragReject: isDropzoneDragReject,
    fileRejections: fileRejections,
    open: openDropzone,
  } = useDropzone({
    onDrop,
    noClick: true,
    accept: ACCEPTED_DRIVE_TYPES,
  });

  useEffect(() => {
    if (fileRejections.length) {
      showError(
        `The following files were rejected: ${fileRejections
          .map((r) => r.file.name)
          .join(", ")}`,
      );
    }
  }, [fileRejections, showError]);

  const subscriptionArgs = useMemo(() => {
    return {
      query: OnDriveFileUpdate,
      variables: {
        workspaceId: workspace.id,
        directoryPath:
          driveTabType === "explorer" ? currentDirectoryPath : undefined,
      },
    };
  }, [workspace.id, driveTabType, currentDirectoryPath]);

  useSubscription({ ...subscriptionArgs, pause: !wsClient });

  const setInternalTextFieldValue = useCallback(
    (value: string) => {
      setSearchParams((params) => {
        const prevValue = params.get("search");
        if ((prevValue || "") === value) {
          return params;
        }
        if (value && value.length) {
          params.set("search", value);
        } else {
          params.delete("search");
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const [searchTextField, setSearchTextFieldState] = useState(searchText);
  const debouncedSetInternalTextFieldValue = useDebounceCallback(
    setInternalTextFieldValue,
    500,
  );
  const setSearchTextField = useCallback(
    (value: string) => {
      setSearchTextFieldState(value);
      debouncedSetInternalTextFieldValue(value);
    },
    [debouncedSetInternalTextFieldValue],
  );
  const setSearchTextFieldImmediate = useCallback(
    (value: string) => {
      setSearchTextFieldState(value);
      setInternalTextFieldValue(value);
    },
    [setInternalTextFieldValue],
  );

  const clearFilters = useCallback(() => {
    setSearchParams((params) => {
      params.delete("type");
      return params;
    });
    setSearchTextFieldImmediate("");
  }, [setSearchParams, setSearchTextFieldImmediate]);

  const { queueState } = useDriveUploadQueueStore(
    useShallow((state) => ({
      queueState: state.queueState,
    })),
  );

  useEffect(() => {
    switch (queueState) {
      case "uploading":
        // On upload start: remove filters that would prevent the user finding his new file
        if (itemTypeFilter !== "file") {
          setSearchParams((params) => {
            if ((params.get("type") as ItemTypeFilterType) !== "file") {
              params.delete("type");
            }
            return params;
          });
        }
        if (searchTextField) {
          setSearchTextFieldImmediate("");
        }
        break;
      case "uploaded":
        reloadObjects();
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueState]);

  const toggleExplorerView = () => {
    const newViewType = driveTabType === "explorer" ? "all" : "explorer";
    setSearchParams((params) => {
      params.set("tab", newViewType);
      return params;
    });
  };

  const onClickNewFile = () => {
    openDropzone();
  };

  const onClickUploadFile = () => {
    setIsNewFileDialogOpen(true);
  };

  const onClickNewStyle = async () => {
    const { style } = await createStyle({
      workspaceId: workspace.id,
      directory: currentDirectoryPath,
      trainingConfig: {
        useImageCaptions: true,
      },
    });
    if (style?.slug) {
      navigate(`/${workspace.name}/models/${style.slug}/create`);
    }
  };

  const onClickNewAsset = () => {
    const createNewAsset = async () => {
      // We create a new session and an asset in the current directory. This is why we cannot redirect simply to the Canvas page.
      const createSessionResult = await createSession({
        createSessionInput: {
          workspaceId: workspace.id,
          name: DEFAULT_FORGE_SESSION_NAME,
          inferenceFormState: {
            activeMode: "CANVAS_2_D",
          },
        },
      });

      const { data: sessionData, error: sessionError } = getResponseFromState({
        state: createSessionResult,
        errorMessage: "Failed to create session",
        onError: (err) => {
          console.error("error creating new session", err);
        },
      });

      if (sessionError || !sessionData?.session) {
        console.error("Failed to create session");
        return;
      }

      const result = await createAsset({
        createAssetInput: {
          workspaceId: workspace.id,
          directory: currentDirectoryPath,
          name: DEFAULT_FORGE_SESSION_NAME,
          sessionId: sessionData.session.id,
        },
      });

      const { data, error } = getResponseFromState({
        state: result,
        errorMessage: "Failed to create asset",
        onError: (err) => {
          console.error("error creating new asset", err);
        },
      });

      if (error || !data?.createAsset) {
        console.error("Failed to create asset");
        return;
      }

      const resp = data.createAsset;

      navigate(
        getForgeSessionUrl({
          workspaceName: workspace.name,
          sessionId: resp.sessionId ?? "",
          assetId: resp.id,
        }),
      );
    };

    createNewAsset();
  };

  const onClickNewDirectory = () => {
    setIsNewDirectoryDialogOpen(true);
  };

  const onDownloadFiles = async (
    files: DriveFileFragment[],
    { isExport }: DownloadOptions,
  ) => {
    if (!files.length) {
      return;
    }

    if (isExport && canExportObjects(files)) {
      await exportObjects(files);
    } else {
      const downloadable = files.reduce<DownloadableObject[]>((acc, f) => {
        if (f.url && f.name) {
          acc.push({ __typename: f.__typename, url: f.url, name: f.name });
        }
        return acc;
      }, []);
      downloadObjects(downloadable);
    }
  };

  const handleNewFileDialogClose = () => {
    setIsNewFileDialogOpen(false);
  };

  const handleNewDirectoryDialogClose = () => {
    setIsNewDirectoryDialogOpen(false);
    createMenuState.onClose();
  };

  const onBodyClick = () => {
    setSelectedItems(null, []);
  };

  const mouseSensor = useSensor(MouseSensor, sensorOptions);
  const touchSensor = useSensor(TouchSensor, sensorOptions);
  const sensors = useSensors(mouseSensor, touchSensor);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const sourceItem = event.active.data.current?.item as
        | DriveItem
        | undefined;
      if (!sourceItem) return;
      if (selectedItems.some((x) => x.id === sourceItem.id)) {
        // then we are dragging a selected item
      } else {
        setSelectedItems(sourceItem.id, items);
      }
    },
    [selectedItems, setSelectedItems, items],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const targetItem = event.over?.data?.current?.item as
        | DriveItem
        | undefined;
      const targetPath = event.over?.data?.current?.path as string | undefined;
      const moveToDirectoryInput: {
        targetDirectoryId?: string;
        workspaceId?: string;
        targetDirectoryPath?: string;
        fileIds: string[];
        assetIds: string[];
        directoryIds: string[];
        styleIds: string[];
      } = {
        targetDirectoryId: undefined,
        workspaceId: undefined,
        targetDirectoryPath: undefined,
        fileIds: selectedItems
          .filter((i) => i.__typename === "File")
          .map((x) => x.id),
        assetIds: selectedItems
          .filter((i) => i.__typename === "Asset")
          .map((x) => x.id),
        directoryIds: selectedItems
          .filter((i) => i.__typename === "Directory")
          .map((x) => x.id),
        styleIds: selectedItems
          .filter(
            (i) => i.__typename === "Style" || i.__typename === "ReferenceSet",
          )
          .map((x) => x.id),
      };
      if (targetItem?.__typename === "Directory") {
        moveToDirectoryInput.targetDirectoryId = targetItem.id;
      } else if (targetPath) {
        moveToDirectoryInput.workspaceId = workspace.id;
        moveToDirectoryInput.targetDirectoryPath = targetPath;
      } else {
        return;
      }
      moveToDirectory({ moveToDirectoryInput }).then((resp) => {
        if (resp.error) {
          console.error(resp.error);
        } else if (resp.data?.moveToDirectory?.__typename === "Error") {
          showError(resp.data.moveToDirectory.message);
        } else {
          reloadObjects();
          setSelectedItems(null, []);
        }
      });
    },
    [
      selectedItems,
      moveToDirectory,
      workspace.id,
      showError,
      reloadObjects,
      setSelectedItems,
    ],
  );

  const handleViewChange = (
    _event: React.SyntheticEvent,
    value: string | null,
  ) => {
    if (value) {
      setSearchParams((params) => {
        params.set("viewType", value);
        return params;
      });
    }
  };

  const handleItemDoubleClick = (object: DriveItem) => {
    if (viewLocation !== "drive") {
      // We are in file picker mode, so we want to select the item
      onActivateItem(object);
      return;
    }

    // We want to open a preview for files and assets
    // TODO: Do we? Should we open the canvas for files and assets instead?
    if (object.__typename === "File" || object.__typename === "Asset") {
      if (selectedItems.every((x) => x !== object)) {
        setSelectedItems(object.id, items);
      }
      setIsPreviewOpen(true);
    } else {
      setSelectedItems(null, []);
      onActivateItem(object);
    }
  };

  const getBorderColor = () => {
    if (isDropzoneDragAccept) return "success.main";
    if (isDropzoneDragReject) return "error.main";
    if (isDropzoneDragActive) return "secondary.4";
    return "secondary.6";
  };

  const rootProps = omit(
    getDropzoneRootProps({
      isDragActive: isDropzoneDragActive,
      isDragAccept: isDropzoneDragAccept,
      isDragReject: isDropzoneDragReject,
    }),
    ["isDragActive", "isDragAccept", "isDragReject"],
  );

  const dialogRootProps = isNewFileDialogOpen ? rootProps : {};
  const mainRootProps = !isNewFileDialogOpen ? rootProps : {};
  const createMenuState = useMenuState({ delay: 200 });

  // In the Grid view, we leave the sidebar open at all times to avoid weird resizing on element click.
  // The list view does not have this issue, so we only show the sidebar when there are selected items.
  const isSelectionSidebarOpen =
    viewLocation === "drive" &&
    (viewType === "grid" || selectedItems.length > 0);

  const canOpenGallery = viewLocation === "drive";

  let columnCount = 1;
  if (viewType === "grid") {
    if (isMedium) {
      columnCount = 4;
    } else if (isLarge) {
      columnCount = 6;
    } else {
      columnCount = 8;
    }
  }

  const menuItems = [];
  if (onClickNewDirectory) {
    menuItems.push(
      <MenuItem
        key="drive-manger-menu-item-newfolder"
        data-actionid="NEW_FOLDER"
        onClick={() => onClickNewDirectory()}
        icon={<Icon icon={faFolder} />}
      >
        New Folder
      </MenuItem>,
    );
  }
  if (onClickNewStyle) {
    menuItems.push(
      <MenuItem
        key="drive-manger-menu-item-newstyle"
        data-actionid="NEW_STYLE"
        onClick={() => onClickNewStyle()}
        icon={<Icon icon={faPalette} />}
      >
        New Model
      </MenuItem>,
    );
  }
  if (onClickNewAsset) {
    menuItems.push(
      <MenuItem
        key="drive-manger-menu-item-newasset"
        data-actionid="NEW_ASSET"
        onClick={() => onClickNewAsset()}
        icon={<Icon icon={faPen} />}
      >
        New Asset
      </MenuItem>,
    );
  }

  return (
    <>
      <Stack
        direction="column"
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        {viewLocation !== "file-picker" && (
          <PrimaryNavigationHeaderRow hideToggle={viewLocation === "dialog"}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <DriveBreadcrumbs />
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                }}
              >
                <Button
                  data-testid="drive-upload-btn"
                  onClick={onClickUploadFile}
                  startIcon={
                    <Icon
                      icon={faArrowUpToLine}
                      style={{
                        color: theme.palette.secondary[1],
                        width: "16px",
                      }}
                    />
                  }
                  size="small"
                  variant="contained"
                  color="secondary"
                >
                  Upload
                </Button>

                <Button
                  data-testid="drive-create-menu-btn"
                  {...bindMenuTrigger(createMenuState)}
                  startIcon={
                    <Icon
                      icon={faPlus}
                      style={{
                        color: theme.palette.secondary[1],
                      }}
                    />
                  }
                  endIcon={
                    <Icon
                      size="small"
                      icon={faAngleDown}
                      style={{
                        width: "13px",
                        fontSize: "10px",
                        color: theme.palette.secondary[3],
                      }}
                    />
                  }
                  size="small"
                  variant="contained"
                  color="secondary"
                >
                  Create
                </Button>

                <Menu data-testid="drive-menu" {...bindMenu(createMenuState)}>
                  {menuItems}
                </Menu>
              </Stack>
            </Stack>
          </PrimaryNavigationHeaderRow>
        )}
        <NavigationHeaderRow>
          {viewLocation === "file-picker" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <DriveBreadcrumbs />
            </Box>
          ) : (
            <Tabs
              value={itemTypeFilter}
              size="small"
              onChange={(event, value) => {
                if (value) {
                  setSearchParams((params) => {
                    if (value === "ALL") {
                      params.delete("type");
                    } else {
                      params.set("type", value);
                    }
                    return params;
                  });
                }
              }}
            >
              <Tab value="ALL" label="All" icon={<Icon icon={faGrid2} />} />
              <Tab
                value="file"
                label="Files"
                icon={<Icon icon={faImagePolaroid} />}
              />
              <Tab value="asset" label="Assets" icon={<Icon icon={faPen} />} />
              <Tab
                value="style"
                label="Models"
                icon={<Icon icon={faPalette} />}
              />
            </Tabs>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              columnGap: 1,
            }}
          >
            <SearchInputField
              value={searchTextField}
              onChange={(value) => setSearchTextField(value)}
              onClear={() => setSearchTextFieldImmediate("")}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <SortDirectionToggle
                value={sortDirection}
                sortField={sortField}
                onChange={(dir) => {
                  setSearchParams((params) => {
                    params.set("dir", dir);
                    return params;
                  });
                }}
              />
              <SortFieldSelect
                fields={sortFieldData.filter(
                  (fieldData) =>
                    !!searchText?.length || fieldData.id !== "rank",
                )}
                selected={sortField}
                onChange={(value) => {
                  if (value) {
                    setSearchParams((params) => {
                      params.set("sort", value);
                      return params;
                    });
                  }
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={toggleExplorerView}
                size="medium"
                variant="tertiary"
                aria-label={
                  driveTabType === "explorer"
                    ? "Organizing by Folders (Click to turn off)"
                    : "Organize by Folders"
                }
                sx={{
                  fontSize: "13px",
                  color:
                    driveTabType === "explorer" ? "secondary.1" : "secondary.2",
                  borderColor:
                    driveTabType === "explorer" ? "secondary.4" : "secondary.6",
                  background:
                    driveTabType === "explorer"
                      ? theme.palette.secondary[7]
                      : "transparent",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  "&:hover": {
                    borderColor: theme.palette.secondary[5],
                    background: theme.palette.secondary[7],
                    position: "relative",
                    zIndex: 1,
                  },
                  fontWeight: 400,
                }}
              >
                <Icon
                  style={{ fontSize: "13px" }}
                  size="large"
                  icon={driveTabType === "explorer" ? faFolderSolid : faFolder}
                />
              </IconButton>
            </Box>

            <Box>
              <Tabs
                sx={{
                  boxShadow: `inset 0 0 0 1px ${theme.palette.secondary[6]}`,

                  "& .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root.Mui-selected":
                    {
                      boxShadow: `inset 0 0 0 1px ${theme.palette.secondary[5]}`,
                    },
                }}
                size="medium"
                value={viewType}
                onChange={handleViewChange}
              >
                {viewTypes.map((viewTypeData) => (
                  <Tab
                    key={viewTypeData.id}
                    icon={viewTypeData.icon}
                    value={viewTypeData.id}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
        </NavigationHeaderRow>
        <Stack
          direction="row"
          sx={{
            width: "100%",
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <DndContext
            collisionDetection={pointerWithin}
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                {...mainRootProps}
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  overflow: "hidden",
                }}
                onClick={onBodyClick}
              >
                <input {...getDropzoneInputProps()} />

                {isFetching && !!items.length && (
                  <InfiniteScrollFetchingIndicator
                    data-testid="drive-loading-indicator"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: 0,
                      right: 0,
                      marginLeft: "auto",
                      marginRight: "auto",
                      zIndex: 10,
                    }}
                  />
                )}

                <DriveItems
                  workspace={workspace}
                  objects={items}
                  refreshView={reloadObjects}
                  onActivateItem={handleItemDoubleClick}
                  onDownloadFiles={onDownloadFiles}
                  fetching={isFetching}
                  hasNextPage={hasMorePages}
                  isNextPageLoading={isFetching}
                  loadNextPage={fetchMoreItems}
                  columnCount={columnCount}
                  isFiltered={isFiltered}
                  onClearFilters={clearFilters}
                />

                {!isNewFileDialogOpen && isDropzoneDragActive && (
                  <DropzoneOverlay data-testid="drive-dropzone">
                    <Stack
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ userSelect: "none" }}
                    >
                      <Typography
                        variant="large"
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          opacity: 0.9,
                        }}
                      >
                        Drop your files here
                      </Typography>
                      <Typography
                        variant="small"
                        sx={{ textAlign: "center", opacity: 0.75 }}
                      >
                        Up to 20 files, max 10 MB each.
                      </Typography>
                    </Stack>
                  </DropzoneOverlay>
                )}
              </Box>

              <DriveUploadQueue
                workspaceId={workspace.id}
                directory={currentDirectoryPath}
              />

              <DragOverlay
                modifiers={[modifierStartFromActivator]}
                dropAnimation={null}
              >
                <DriveDragOverlay />
              </DragOverlay>

              <Dialog
                open={isNewFileDialogOpen}
                onClose={handleNewFileDialogClose}
                sx={{
                  "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                      width: "480px",
                    },
                  },
                }}
              >
                <Box>
                  <DialogContent
                    sx={{ px: 4, pt: 4, pb: 2 }}
                    {...dialogRootProps}
                  >
                    <Box
                      component="button"
                      onClick={onClickNewFile}
                      sx={{
                        width: "100%",
                        height: "100%",
                        background: "none",
                        border: "none",
                        p: 0,
                      }}
                    >
                      <DropzoneDialogContent
                        className="group"
                        sx={{ borderColor: getBorderColor() }}
                      >
                        <DriveEmptyState />
                      </DropzoneDialogContent>
                    </Box>
                  </DialogContent>
                  <DialogActions
                    sx={{
                      justifyContent: "center",
                      width: "100%",
                      pt: 2,
                    }}
                  >
                    <Button
                      onClick={handleNewFileDialogClose}
                      variant="contained"
                      color="secondary"
                      sx={{
                        minWidth: 100,
                      }}
                    >
                      Done
                    </Button>
                  </DialogActions>
                </Box>
              </Dialog>
            </Box>
          </DndContext>
          {isSelectionSidebarOpen && (
            <SelectionDrawer
              context="drive"
              objects={selectedItems}
              isGalleryOpen={isPreviewOpen}
              onGalleryOpen={() => setIsPreviewOpen(true)}
              onReloadObjects={reloadObjects}
              onClose={() => {
                setSelectedItems(null, []);
                setIsPreviewOpen(false);
              }}
            />
          )}
        </Stack>
      </Stack>
      <DriveNewDirectoryDialog
        isOpen={isNewDirectoryDialogOpen}
        onClose={handleNewDirectoryDialogClose}
        workspaceId={workspace.id}
      />
      {isPreviewOpen && canOpenGallery && previewObject && (
        <GalleryWindow
          object={previewObject}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </>
  );
};
